// TERRA — the open battlefield generator (campaign battles). Replaces the tile maze with a
// sculpted natural heightfield in the biome of the target world, while satisfying every contract
// the tile system fulfilled: a truly-flat ≥1600×1200 start pad (and core pad) at topY(0)=200, a
// NavGraph the armies/tanks/player/projectiles consume unchanged (heightAt: null = unwalkable),
// guaranteed start→core connectivity, lane gates that spread the armies across the natural passes,
// pseudo-tiles for the minimap, and GeneratedLevel-shaped stats for the panel.
//
// Design rules from the adversarial review:
//  • walkability and shot-occlusion are SEPARATE height functions (flat hazards block walking,
//    never bullets); • path costs are weighted (lava is expensive, not forbidden — tough units
//    ford, grunts go around); • crevasses are DASHED so ice maps stay connected; • passes are
//    explicitly validated (2..4) with synthetic lane gates; • retry first, carve as last resort.

import { CELL, topY, levelPlan, type LevelPlan } from "../config";
import { FACTORY } from "../factory/defs";
import type { LaneDiamond, Tile, LevelStats } from "../map/grammar";
import type { NavGraph, Pt } from "../systems/pathfind";
import { levelSeed, Rng } from "../engine/rng";
import { fbm, warpFbm, ridged, worleyEdge2, smoothstep, lerp } from "./noise";
import { tessCell, sampleTess, type CTri } from "./contour";

// hazard bytes (stored at GRID resolution so standing IN a channel is distinguishable)
export const HAZ_NONE = 0;
export const HAZ_LAVA = 1; // walkable, burns (cost 8)
export const HAZ_LAVA_DEEP = 2; // unwalkable pool
export const HAZ_CREVASSE = 3; // unwalkable crack
export const HAZ_WATER_DEEP = 4; // unwalkable
export const HAZ_WATER_SHALLOW = 5; // walkable, slow (cost 2)
export const HAZ_SLICK = 6; // walkable, cheap for NPCs (cost 0.6), player drifts

export const GRID = 50; // height-grid step (studs); mesh vertices sit exactly on these nodes
const PAD_Y = topY(0); // 200 — the shop hardcodes this floor; both pads sit here
const SLOPE_MAX = 0.5; // max walkable rise/run (~26.5°)
const STEP_MAX = 120; // max |Δh| between adjacent nav-cell centers (> tan(26.5°)*200≈100 + noise)
const ATTEMPTS = 8; // seed perturbation retries before the carve fallback fires

export interface TerraStyleId { style: 0 | 1 | 2 | 3 } // garden / rocky / scorched / ice

export interface TerraField {
  minX: number; maxX: number; minZ: number; maxZ: number;
  n: number; // grid nodes per side (heights is (n+1)²)
  heights: Float32Array;
  hazards: Uint8Array;
  glow: Float32Array; // 0..1 lava/crack emissive mask baked at grid nodes (shader attr)
  pad: Float32Array; // 0..1 built-platform mask (deck-plate shading)
  feat: Float32Array; // 0..1 sharp-feature (lava channel/pool, crevasse) intensity — the marching-squares field
  contourTess: Map<number, CTri[]>; // cell index (gz*n+gx) → contour triangulation (mesh + heightAt share it)
  style: number;
  seaY: number; // garden water plane height (NaN when none)
  /** Raw surface height — smooth, defined everywhere (far field analytic). Never null. */
  heightAt(x: number, z: number): number;
  hazardAt(x: number, z: number): number;
}

export interface TerraLevel {
  // --- GeneratedLevel-compatible surface (minimap / panel / framing / console) ---
  level: number;
  seedWord: string;
  seedInt: number;
  tiles: Tile[]; // pseudo-tiles: one per walkable nav cell (minimap elevation/hazard map)
  cells: Map<string, never>; // unused in terra mode (nav is provided directly)
  start: { x: number; z: number; level: number };
  exit: { x: number; z: number; level: number };
  lanes: LaneDiamond[];
  stats: LevelStats;
  plan: LevelPlan;
  reachedTarget: boolean;
  // --- terra-specific ---
  nav: NavGraph;
  field: TerraField;
  padGap: number;
  carved: boolean; // true when the connectivity fallback had to fire
  /** The exact PAD RECTS the terrain stamped (world space). The factory sites its build grid CONCENTRIC with
   *  its own rect — deriving the grid independently from nav cells (CELL=200) let the two disagree by up to
   *  half a cell, which is why the cement never lined up with the buildable area. */
  startRect: { x0: number; x1: number; z0: number; z1: number };
  coreRect: { x0: number; x1: number; z0: number; z1: number };
}

const key = (cx: number, cz: number): string => `${cx},${cz}`;

/** Style id from the biome key (matches planetsurface's style numbering). */
export function styleOf(biomeKey: string): 0 | 1 | 2 | 3 {
  return biomeKey === "garden" ? 0 : biomeKey === "temperate" ? 1 : biomeKey === "hot" ? 2 : 3;
}

interface TerraPlan {
  padGap: number; // pad EDGE-to-edge march distance (the difficulty ramp's main knob)
  openFrac: number; // fraction of the field that reads open/gentle
  passTarget: [number, number]; // acceptable pass count range
  hazardAmp: number; // biome hazard coverage multiplier
  coreLift: number; // core pad elevation above 200 (late-level high-ground finale)
  size: number; // field span (studs, square)
}

function terraPlan(level: number): TerraPlan {
  const L = Math.max(1, Math.floor(level));
  const step = L - 1;
  const padGap = Math.min(5600 + step * 420, 9200);
  const size = Math.ceil((padGap + 2 * 1800 + 2 * 1000) / CELL) * CELL; // pads + margins, CELL-aligned
  return {
    padGap,
    size: Math.min(size, 14400),
    openFrac: Math.max(0.4, 0.62 - step * 0.02),
    passTarget: [2, 4],
    hazardAmp: Math.min(1.25, 0.55 + step * 0.06),
    coreLift: L >= 5 ? Math.min(200, (L - 4) * 40) : 0,
  };
}

/** Generate the open battlefield for a campaign battle. Deterministic per (seedWord, level). */
export function generateTerra(seedWord: string, level: number, biomeKey: string): TerraLevel {
  const plan = terraPlan(level);
  const style = styleOf(biomeKey);
  const baseSeed = levelSeed(seedWord, level);
  let best: TerraLevel | null = null;
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    const t = generateOnce(seedWord, level, style, plan, (baseSeed ^ Math.imul(attempt, 0x9e3779b1)) >>> 0, false);
    if (t) return t;
    if (attempt === ATTEMPTS - 1) {
      // last resort: regenerate attempt 0 with the carve fallback enabled (guaranteed connection)
      best = generateOnce(seedWord, level, style, plan, baseSeed, true);
    }
  }
  return best!;
}

function generateOnce(
  seedWord: string, level: number, style: number, plan: TerraPlan, seedInt: number, allowCarve: boolean,
): TerraLevel | null {
  const rng = new Rng(seedInt);
  const S = plan.size;
  const half = S / 2;
  const n = Math.round(S / GRID); // grid cells per side; nodes = n+1
  const heights = new Float32Array((n + 1) * (n + 1));
  const hazards = new Uint8Array((n + 1) * (n + 1));
  const glow = new Float32Array((n + 1) * (n + 1));
  const padMask = new Float32Array((n + 1) * (n + 1));
  const featMask = new Float32Array((n + 1) * (n + 1));
  const sd = seedInt & 0xffff;

  // ---- pads: opposite ends of a seeded CARDINAL axis, sited then stamped flat ----
  const axis = rng.int(0, 3); // 0:+X 1:+Z 2:-X 3:-Z (core direction from start)
  const dirX = axis === 0 ? 1 : axis === 2 ? -1 : 0;
  const dirZ = axis === 1 ? 1 : axis === 3 ? -1 : 0;
  const wobble = (rng.float() - 0.5) * 1200; // lateral offset so the axis isn't dead-center
  // Pad DERIVED from the factory's buildable footprint, and EXACTLY it — the paved plate is the build grid,
  // nothing more. It used to carry a spare tile of margin all round, which read on screen as buildable
  // ground that silently was not: paving that you cannot place on is worse than no paving. Hardcoding any
  // of this is how it drifts — the footprint went 8→6 and every number here still described the old plate.
  // The paved plate IS the buildable grid — same size, same center. That only holds because the factory now
  // sites its grid CONCENTRIC with this rect (see Factory.onLevel's padRect); while the two were derived
  // independently they drifted by up to half a nav cell, the border looked lopsided, and trimming the pad
  // to grid size left the far column of tiles on sloped ground. The cement mask is a hard rect test
  // (field.ts: pad = dS === 0 ? 1 : ...), so what is paved is exactly what is buildable.
  const padHalf = (FACTORY.footprint * FACTORY.tile) / 2; // 6×6 ⇒ 300. The CEMENT IS THE BUILD GRID, exactly.
  const padHalfW = padHalf, padHalfL = padHalf;
  // ...and the blend skirt scales WITH the plate. At a fixed 420 the soft edge was nearly as wide as the
  // pad itself, so the ground read as flat far outside anything you can actually build on.
  const padBlend = padHalf * 0.84;

  const off = plan.padGap / 2 + padHalfL;
  const startC = { x: -dirX * off + (dirZ !== 0 ? wobble : 0), z: -dirZ * off + (dirX !== 0 ? wobble : 0) };
  const coreC = { x: dirX * off + (dirZ !== 0 ? wobble : 0), z: dirZ * off + (dirX !== 0 ? wobble : 0) };
  // pad AABBs: long side perpendicular to the axis
  const rectOf = (c: { x: number; z: number }): { x0: number; x1: number; z0: number; z1: number } =>
    dirX !== 0
      ? { x0: c.x - padHalfL, x1: c.x + padHalfL, z0: c.z - padHalfW, z1: c.z + padHalfW }
      : { x0: c.x - padHalfW, x1: c.x + padHalfW, z0: c.z - padHalfL, z1: c.z + padHalfL };
  const startRect = rectOf(startC);
  const coreRect = rectOf(coreC);
  // shop apron: flat strip extending BEHIND the start pad (away from the core) — the shop building
  // sits ~320 studs beyond the pad's back edge; keep it flat and NPC-unwalkable.
  const apron = {
    x0: dirX !== 0 ? (dirX > 0 ? startRect.x0 - 950 : startRect.x1) : startC.x - 550,
    x1: dirX !== 0 ? (dirX > 0 ? startRect.x0 : startRect.x1 + 950) : startC.x + 550,
    z0: dirZ !== 0 ? (dirZ > 0 ? startRect.z0 - 950 : startRect.z1) : startC.z - 550,
    z1: dirZ !== 0 ? (dirZ > 0 ? startRect.z0 : startRect.z1 + 950) : startC.z + 550,
  };

  const rectDist = (r: { x0: number; x1: number; z0: number; z1: number }, x: number, z: number): number => {
    const dx = Math.max(r.x0 - x, 0, x - r.x1);
    const dz = Math.max(r.z0 - z, 0, z - r.z1);
    return Math.hypot(dx, dz);
  };

  // ---- height synthesis ----
  const relief = style === 3 ? 110 : 140; // rolling amplitude in the open
  const mRelief = style === 1 ? 300 : 240; // rugged/ridge amplitude
  const mf = 1 / 2600; // macro wavelength
  const rf = 1 / 1500; // ridge wavelength
  const coreY = PAD_Y + plan.coreLift;
  const ringW = plan.coreLift > 0 ? 700 : 420; // wider blend for a lifted core (keeps slope walkable)

  const rawH = (x: number, z: number): number => {
    // region masks: open rolling ground vs rugged walls (the "complications")
    const region = warpFbm(x * mf * 0.5 + sd, z * mf * 0.5 - sd, sd + 11, 3, 1.4);
    const apronF = Math.min( // flatten influence near pads so bases sit in buildable ground
      smoothstep(0, 1500, rectDist(startRect, x, z)),
      smoothstep(0, 1500, rectDist(coreRect, x, z)),
    );
    const rugged = smoothstep(plan.openFrac, plan.openFrac + 0.14, region) * apronF;
    let h = PAD_Y + (warpFbm(x * mf + sd, z * mf - sd, sd, 4, 1.6) - 0.5) * 2 * relief * (0.3 + 0.7 * apronF);
    h += Math.pow(ridged(x * rf + sd, z * rf + sd, sd + 37, 4), 2.2) * mRelief * rugged;
    if (style === 1) {
      // HOT-ROCKY MESAS: terraced plateau stamps — steep flanks read as hard cover walls
      const m = smoothstep(0.64, 0.72, warpFbm(x * mf * 0.8 + sd + 99, z * mf * 0.8 + sd + 99, sd + 53, 3, 1.2)) * apronF;
      h = lerp(h, PAD_Y + 265 + (region - 0.5) * 40, m);
    }
    return h;
  };

  const idx = (gx: number, gz: number): number => gz * (n + 1) + gx;
  const seaY = style === 0 ? PAD_Y - 26 : NaN;
  const amp = plan.hazardAmp;

  // ONE continuous field sample used for BOTH the baked mesh grid AND per-frame hazard queries, so the
  // geometry, the gameplay hazard, and the shader all read the identical field (no drift).
  //  • carves are CONTINUOUS smoothstep ramps (no `feat > threshold` STEP), so diagonal/curved feature
  //    edges don't staircase on the 50-stud mesh (fix 3);
  //  • `gl` is the smooth PRE-threshold feature intensity — the shader interpolates it and thresholds it
  //    PER-PIXEL for crisp, grid-independent edges (fix 1), and hazardAt evaluates this analytically at the
  //    exact query point rather than snapping to the nearest node (fix 2).
  const sampleTerra = (x: number, z: number): { h: number; hz: number; gl: number; pad: number; feat: number } => {
    let h = rawH(x, z);
    let hz = HAZ_NONE;
    let gl = 0;
    let feat = 0; // sharp-feature membership (lava channel/pool, crevasse) → the marching-squares contour field

    if (style === 2) {
      // SCORCHED: molten channels along worley edges + pools in the deepest basins
      const e = worleyEdge2(x / 1900 + sd, z / 1900 - sd, sd + 7);
      const gate = fbm(x / 3200 + sd, z / 3200 + sd, sd + 71, 3);
      const chan = (1 - smoothstep(0.03, 0.075 * amp, e)) * smoothstep(0.34, 0.44, gate);
      const chanR = smoothstep(0.18, 0.42, chan); // continuous channel core (replaces the chan>0.25 step)
      // #4: carve SHARPENED, centered on the feat=0.5 marching-squares contour — the mesh tessellation makes
      // this steep drop follow the true curve instead of staircasing. Lava is walkable, so its banks use a
      // wider (fordable) band than the unwalkable crevasse/pool below.
      if (chanR > 0.001) { h -= 58 * smoothstep(0.3, 0.7, chanR); gl = Math.max(gl, chan); feat = Math.max(feat, chanR); if (chanR > 0.4) hz = HAZ_LAVA; }
      if (amp > 0.6) {
        const pool = smoothstep(0.34, 0.26, warpFbm(x / 2100 + sd + 5, z / 2100 + sd + 5, sd + 13, 3, 1.2));
        const poolR = smoothstep(0.42, 0.55, pool);
        if (poolR > 0.001) { h -= (40 + 45 * pool) * smoothstep(0.38, 0.62, poolR); gl = Math.max(gl, poolR); feat = Math.max(feat, poolR); if (poolR > 0.5) hz = HAZ_LAVA_DEEP; }
      }
    } else if (style === 3) {
      // ICE: DASHED crevasses (never closed loops — natural ice bridges survive) + frozen lakes.
      // The gate drives the crack WIDTH, not its depth: where the gate fades, the crack PINCHES SHUT into a
      // full-height ice bridge (reads as intentional), never a half-depth trench ("terrain failing to be a
      // gap"). Inside the crack, depth saturates fast — a gap is a GAP (near-binary floor at -130), with the
      // wall band concentrated at the rim where the marching-squares contour (feat=0.5) tracks the true curve.
      const e = worleyEdge2(x / 1700 + sd, z / 1700 - sd, sd + 7);
      const gate = fbm(x / 1400 - sd, z / 1400 + sd, sd + 71, 3);
      const wMul = smoothstep(0.4, 0.52, gate); // 0 = bridged (crack pinched shut), 1 = full-width crack
      const w = 0.055 * amp * wMul;             // local crack half-width (worley edge-space)
      const crev = w > 1e-4 ? smoothstep(w, w * 0.3, e) : 0; // 1 at the crack center → 0 at the rim
      if (crev > 0.001) { h -= 130 * smoothstep(0.3, 0.55, crev); gl = Math.max(gl, crev * 0.8); feat = Math.max(feat, crev); if (crev > 0.45) hz = HAZ_CREVASSE; }
      if (hz === HAZ_NONE) {
        const lake = smoothstep(0.36, 0.30, fbm(x / 2900 + sd, z / 2900 - sd, sd + 29, 3));
        const lakeR = smoothstep(0.45, 0.55, lake);
        if (lakeR > 0.001) { h = lerp(h, PAD_Y - 20, 0.92 * lakeR); if (lakeR > 0.5) hz = HAZ_SLICK; }
      }
    } else if (style === 0 && h < seaY) {
      // GARDEN: standing water — deep is impassable, the shore band wades slow
      hz = h < seaY - 22 ? HAZ_WATER_DEEP : HAZ_WATER_SHALLOW;
    }

    // ---- pad stamping (truly flat, always wins over hazards) ----
    const dS = rectDist(startRect, x, z);
    const dC = rectDist(coreRect, x, z);
    const dA = rectDist(apron, x, z);
    const wS = 1 - smoothstep(0, padBlend, dS);
    const wC = 1 - smoothstep(0, ringW, dC);
    const wA = 1 - smoothstep(0, 380, dA);
    if (wS > 0) { h = lerp(h, PAD_Y, wS); if (dS === 0) { h = PAD_Y; hz = HAZ_NONE; gl = 0; feat = 0; } }
    if (wA > 0) { h = lerp(h, PAD_Y, wA); if (dA === 0) { h = PAD_Y; hz = HAZ_NONE; gl = 0; feat = 0; } }
    if (wC > 0) { h = lerp(h, coreY, wC); if (dC === 0) { h = coreY; hz = HAZ_NONE; gl = 0; feat = 0; } }
    if (Math.max(wS, wC) > 0.55) { hz = HAZ_NONE; gl = 0; feat = 0; } // hazard-free ring around each base
    const pad = dS === 0 || dC === 0 ? 1 : dA === 0 ? 0.8 : 0;
    return { h, hz, gl, pad, feat };
  };

  for (let gz = 0; gz <= n; gz++) {
    for (let gx = 0; gx <= n; gx++) {
      const s = sampleTerra(gx * GRID - half, gz * GRID - half);
      const i = idx(gx, gz);
      heights[i] = s.h;
      hazards[i] = s.hz;
      glow[i] = s.gl;
      padMask[i] = s.pad;
      featMask[i] = s.feat;
    }
  }

  // ---- marching-squares tessellation of feature-crossing cells (#4): the mesh AND heightAt both consume
  // these, so the sharpened feature geometry follows the true iso-contour with NO 50-stud staircase while
  // mesh height ≡ unit feet ≡ nav height stays exact. Triangulation is keyed on featMask (which carveRoute
  // never touches) and stores CORNER REFS, so it interpolates the LIVE heights[] — valid even after carving. ----
  const FEAT_ISO = 0.5;
  const contourTess = new Map<number, CTri[]>();
  for (let gz = 0; gz < n; gz++) {
    for (let gx = 0; gx < n; gx++) {
      const i0 = gz * (n + 1) + gx;
      const f0 = featMask[i0], f1 = featMask[i0 + 1], f2 = featMask[i0 + (n + 1)], f3 = featMask[i0 + (n + 1) + 1];
      const nin = (f0 > FEAT_ISO ? 1 : 0) + (f1 > FEAT_ISO ? 1 : 0) + (f2 > FEAT_ISO ? 1 : 0) + (f3 > FEAT_ISO ? 1 : 0);
      if (nin === 0 || nin === 4) continue; // no contour in this cell → standard grid split
      contourTess.set(gz * n + gx, tessCell(f0, f1, f2, f3, FEAT_ISO));
    }
  }

  // ---- field object (triangle-consistent height everywhere; analytic continuation outside) ----
  const minX = -half, minZ = -half, maxX = half, maxZ = half;
  const heightAt = (x: number, z: number): number => {
    const fx = (x - minX) / GRID, fz = (z - minZ) / GRID;
    if (fx < 0 || fz < 0 || fx > n || fz > n) return rawH(x, z); // analytic far field (skirt)
    const gx = Math.min(n - 1, Math.floor(fx)), gz = Math.min(n - 1, Math.floor(fz));
    const u = fx - gx, v = fz - gz;
    const i0 = gz * (n + 1) + gx;
    // corners 0=(gx,gz) 1=(gx+1,gz) 2=(gx,gz+1) 3=(gx+1,gz+1) — matches contour.ts + the mesh
    const cH = [heights[i0], heights[i0 + 1], heights[i0 + (n + 1)], heights[i0 + (n + 1) + 1]];
    const tess = contourTess.get(gz * n + gx);
    if (tess) {
      const ht = sampleTess(tess, u, v, cH); // EXACT surface the mesh renders in a contour cell
      if (ht != null) return ht;
    }
    // standard a-d-b / b-d-e split (u+v<=1 diagonal) — matches the mesh's non-contour cells + the old sampler
    return u + v <= 1
      ? cH[0] + (cH[1] - cH[0]) * u + (cH[2] - cH[0]) * v
      : cH[3] + (cH[2] - cH[3]) * (1 - u) + (cH[1] - cH[3]) * (1 - v);
  };
  let carved = false; // set true only if carveRoute fires (last-resort connectivity fallback, below)
  const hazardAt = (x: number, z: number): number => {
    if (x < minX || z < minZ || x > maxX || z > maxZ) return HAZ_NONE;
    if (carved) {
      // a carved level MUTATED the baked arrays (cleared a corridor across hazards); read them via node-snap
      // so the hazard verdict matches the carve-flattened mesh + the walkable route — the analytic feature
      // field is carve-blind and would burn units on the one guaranteed passage (review finding).
      const gx = Math.round((x - minX) / GRID), gz = Math.round((z - minZ) / GRID);
      return hazards[gz * (n + 1) + gx];
    }
    // otherwise evaluate the SAME continuous field the mesh + shader use, at the exact point — so a unit's
    // hazard verdict follows the true diagonal/curved feature edge instead of snapping to a 50-stud node (fix 2).
    return sampleTerra(x, z).hz;
  };
  const field: TerraField = { minX, maxX, minZ, maxZ, n, heights, hazards, glow, pad: padMask, feat: featMask, contourTess, style, seaY, heightAt, hazardAt };

  // ---- walkability + cost at nav CELL resolution ----
  const c0x = Math.floor(minX / CELL), c0z = Math.floor(minZ / CELL);
  const c1x = Math.ceil(maxX / CELL), c1z = Math.ceil(maxZ / CELL);
  const walk = new Map<string, number>(); // key -> cost (present = walkable)
  const cellH = new Map<string, number>();
  const inApron = (x: number, z: number): boolean => x > apron.x0 && x < apron.x1 && z > apron.z0 && z < apron.z1;
  // The ONE walkability rule (used by the main pass AND carveRoute's re-walk, so they can't drift):
  // slope from 5 height samples; hazard = the max over the FULL 5×5 grid-node block covering the
  // cell (a ±70 point sample aliased to ±50 nodes and missed the boundary ring — review finding).
  const evalCell = (cx: number, cz: number): { cost: number; h: number } | null => {
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    if (wx < minX + CELL || wx > maxX - CELL || wz < minZ + CELL || wz > maxZ - CELL) return null; // border margin
    if (inApron(wx, wz)) return null; // the shop building's ground — players only
    const hc = heightAt(wx, wz);
    const h1 = heightAt(wx - 90, wz), h2 = heightAt(wx + 90, wz), h3 = heightAt(wx, wz - 90), h4 = heightAt(wx, wz + 90);
    const slope = Math.max(Math.abs(h1 - hc), Math.abs(h2 - hc), Math.abs(h3 - hc), Math.abs(h4 - hc)) / 90;
    if (slope > SLOPE_MAX) return null;
    let hzWorst = HAZ_NONE;
    const g0x = Math.max(0, Math.round((wx - 100 - minX) / GRID)), g0z = Math.max(0, Math.round((wz - 100 - minZ) / GRID));
    for (let gz = g0z; gz <= Math.min(n, g0z + 4); gz++)
      for (let gx = g0x; gx <= Math.min(n, g0x + 4); gx++) hzWorst = Math.max(hzWorst, hazards[gz * (n + 1) + gx]);
    if (hzWorst === HAZ_LAVA_DEEP || hzWorst === HAZ_CREVASSE || hzWorst === HAZ_WATER_DEEP) return null;
    let cost = 1 + slope * 1.6; // gentle slope preference → armies flow along valleys
    if (hzWorst === HAZ_LAVA) cost = 8;
    else if (hzWorst === HAZ_WATER_SHALLOW) cost = 2.2;
    else if (hzWorst === HAZ_SLICK) cost = 0.65; // frozen lakes are highways (readable + risky)
    return { cost, h: hc };
  };
  for (let cz = c0z; cz < c1z; cz++) {
    for (let cx = c0x; cx < c1x; cx++) {
      const r = evalCell(cx, cz);
      if (!r) continue;
      const k = key(cx, cz);
      walk.set(k, r.cost);
      cellH.set(k, r.h);
    }
  }

  // pad cells (start/core keys)
  const startKeys: string[] = [];
  const coreKeys: string[] = [];
  for (const [k] of walk) {
    const [cx, cz] = k.split(",").map(Number);
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    // interior cells only (full cell inside the flat stamp)
    if (rectDist(startRect, wx - 100, wz - 100) === 0 && rectDist(startRect, wx + 100, wz + 100) === 0) startKeys.push(k);
    if (rectDist(coreRect, wx - 100, wz - 100) === 0 && rectDist(coreRect, wx + 100, wz + 100) === 0) coreKeys.push(k);
  }
  if (!startKeys.length || !coreKeys.length) return null;

  // ---- adjacency + connectivity ----
  const adjOf = (k: string): string[] => {
    const [cx, cz] = k.split(",").map(Number);
    const h0 = cellH.get(k)!;
    const out: string[] = [];
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nk = key(cx + dx, cz + dz);
      const h1 = cellH.get(nk);
      if (h1 === undefined) continue;
      if (Math.abs(h1 - h0) > STEP_MAX) continue;
      out.push(nk);
    }
    return out;
  };
  const flood = (): Set<string> => {
    const seen = new Set<string>([startKeys[0]]);
    const q = [startKeys[0]];
    let head = 0;
    while (head < q.length) for (const nb of adjOf(q[head++])) if (!seen.has(nb)) { seen.add(nb); q.push(nb); }
    return seen;
  };
  let reach = flood();
  if (!reach.has(coreKeys[0])) {
    if (!allowCarve) return null; // retry with a perturbed seed
    carveRoute(field, walk, cellH, startKeys[0], coreKeys[0], evalCell);
    carved = true;
    reach = flood();
    if (!reach.has(coreKeys[0])) return null; // should not happen; outer wrapper keeps the best
  }

  // ---- passes: cluster mid-line crossings into corridors; need 2..4 (else retry) ----
  const midKeys: string[] = [];
  for (const k of reach) {
    const [cx, cz] = k.split(",").map(Number);
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    const along = dirX !== 0 ? (wx - (startC.x + coreC.x) / 2) * dirX : (wz - (startC.z + coreC.z) / 2) * dirZ;
    if (Math.abs(along) < CELL) midKeys.push(k);
  }
  // cluster by the perpendicular coordinate
  midKeys.sort((a, b) => {
    const pa = perpOf(a, dirX), pb = perpOf(b, dirX);
    return pa - pb;
  });
  const passes: string[][] = [];
  let cur: string[] = [];
  let lastPerp = -Infinity;
  for (const k of midKeys) {
    const p = perpOf(k, dirX);
    if (p - lastPerp > 2) { if (cur.length) passes.push(cur); cur = []; }
    cur.push(k);
    lastPerp = p;
  }
  if (cur.length) passes.push(cur);
  // pass WIDTH = distinct perpendicular columns (the mid-line band always spans 2 along-axis cell
  // columns, so raw length would double-count and let a 1-cell chokepoint masquerade as "wide")
  const widePasses = passes.filter((p) => new Set(p.map((k) => perpOf(k, dirX))).size >= 2);
  // the final (allowCarve) attempt must ACCEPT the map even when the pass-count gate fails —
  // returning null here soft-locked deterministic seeds (review finding, verified on 6/96 configs);
  // a 1-pass map degrades gracefully below (no lane diamonds).
  if (!allowCarve && !carved && (widePasses.length < plan.passTarget[0] || widePasses.length > plan.passTarget[1])) return null;

  // synthetic lane diamonds: one gate (mid cell) per pass → laneVias spreads the armies for free
  const gates = (widePasses.length ? widePasses : passes).slice(0, 4).map((p) => p[Math.floor(p.length / 2)]);
  const lanes: LaneDiamond[] = gates.length >= 2 ? [{ lanes: gates.map((g) => [g]) }] : [];

  // ---- NavGraph (the exact interface every consumer already speaks) ----
  const adj = new Map<string, string[]>();
  const point = new Map<string, Pt>();
  const levelBand = new Map<string, number>();
  for (const k of walk.keys()) {
    const [cx, cz] = k.split(",").map(Number);
    adj.set(k, adjOf(k));
    point.set(k, { x: (cx + 0.5) * CELL, y: cellH.get(k)!, z: (cz + 0.5) * CELL });
    levelBand.set(k, Math.round((cellH.get(k)! - PAD_Y) / 200));
  }
  const navHeightAt = (x: number, z: number): number | null => {
    const k = key(Math.floor(x / CELL), Math.floor(z / CELL));
    if (!walk.has(k)) return null;
    return heightAt(x, z);
  };
  const shotHeightAt = (x: number, z: number): number | null =>
    x < minX || x > maxX || z < minZ || z > maxZ ? null : heightAt(x, z);
  // terra string-pull LOS: pure walkability + per-step climb (the tile band-equality test
  // checkerboards on smooth slopes — see design review)
  const smoothLos = (a: Pt, b: Pt): boolean => {
    const dist = Math.hypot(b.x - a.x, b.z - a.z);
    const steps = Math.max(1, Math.ceil(dist / 80));
    let prev = navHeightAt(a.x, a.z);
    if (prev == null) return false;
    for (let s = 1; s <= steps; s++) {
      const px = a.x + ((b.x - a.x) * s) / steps, pz = a.z + ((b.z - a.z) * s) / steps;
      const h = navHeightAt(px, pz);
      if (h == null || Math.abs(h - prev) > 55) return false;
      // never string-pull a shortcut ACROSS a priced hazard the weighted path detoured around
      // (a straight chord over lava would undo the Dijkstra pricing — review finding)
      const hz = hazardAt(px, pz);
      if (hz === HAZ_LAVA || hz === HAZ_WATER_SHALLOW) return false;
      prev = h;
    }
    return true;
  };
  const nav: NavGraph = {
    adj, point, level: levelBand, ramp: new Map(), startKeys, coreKeys, lanes,
    heightAt: navHeightAt, shotHeightAt, cost: walk, smoothLos,
  };

  // ---- pseudo-tiles (minimap): one per walkable cell, kind-tinted by hazard ----
  const tiles: Tile[] = [];
  let tid = 0;
  for (const k of walk.keys()) {
    const [cx, cz] = k.split(",").map(Number);
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    const hzC = hazardAt(wx, wz);
    const kind = rectDist(startRect, wx, wz) === 0 ? "start"
      : rectDist(coreRect, wx, wz) === 0 ? "core"
      : hzC === HAZ_LAVA ? "lava" : hzC === HAZ_SLICK ? "slick" : hzC === HAZ_WATER_SHALLOW ? "water" : "corridor";
    tiles.push({
      id: tid++, kind: kind as Tile["kind"],
      x0: cx * CELL, x1: (cx + 1) * CELL, z0: cz * CELL, z1: (cz + 1) * CELL,
      level: Math.max(0, Math.min(6, levelBand.get(k)! + 1)),
    });
  }

  const stats: LevelStats = {
    tiles: tiles.length, corridors: 0, rooms: 0, corners: 0, ramps: 0, branches: 0, turns: 0,
    arenas: widePasses.length, bridges: 0, chambers: 0,
    pathLength: Math.round(plan.padGap / CELL),
    minElev: 0, maxElev: Math.round(plan.coreLift / 200),
    bbox: { minX, maxX, minZ, maxZ },
  };

  return {
    level, seedWord, seedInt, tiles, cells: new Map<string, never>(), lanes,
    start: { x: startC.x, z: startC.z, level: 0 },
    exit: { x: coreC.x, z: coreC.z, level: Math.round(plan.coreLift / 200) },
    stats, plan: levelPlan(level), reachedTarget: true,
    nav, field, padGap: plan.padGap, carved, startRect, coreRect,
  };
}

function perpOf(k: string, dirX: number): number {
  const [cx, cz] = k.split(",").map(Number);
  return dirX !== 0 ? cz : cx;
}

/** Connectivity fallback: A* over a relaxed cost field (blocked cells are expensive, not
 *  forbidden), then flatten a walkable corridor along the route — heights blend toward a smoothed
 *  profile and hazard bytes are cleared, so the trench reads as a natural draw, not a scar. */
function carveRoute(
  field: TerraField,
  walk: Map<string, number>,
  cellH: Map<string, number>,
  fromK: string,
  toK: string,
  evalCell: (cx: number, cz: number) => { cost: number; h: number } | null,
): void {
  // A* over ALL in-bounds cells with relaxed costs
  const parse = (k: string): [number, number] => k.split(",").map(Number) as [number, number];
  const [tx, tz] = parse(toK);
  const gScore = new Map<string, number>([[fromK, 0]]);
  const prev = new Map<string, string>();
  const openH: Array<{ k: string; f: number }> = [{ k: fromK, f: 0 }];
  const cellCost = (k: string): number => (walk.has(k) ? walk.get(k)! : 40); // blocked = expensive
  const cellHeight = (k: string): number => {
    if (cellH.has(k)) return cellH.get(k)!;
    const [cx, cz] = parse(k);
    return field.heightAt((cx + 0.5) * CELL, (cz + 0.5) * CELL);
  };
  const inBounds = (cx: number, cz: number): boolean => {
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    return wx > field.minX + CELL && wx < field.maxX - CELL && wz > field.minZ + CELL && wz < field.maxZ - CELL;
  };
  let found = false;
  while (openH.length) {
    let bi = 0;
    for (let i = 1; i < openH.length; i++) if (openH[i].f < openH[bi].f) bi = i;
    const { k } = openH.splice(bi, 1)[0];
    if (k === toK) { found = true; break; }
    const [cx, cz] = parse(k);
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nk = `${cx + dx},${cz + dz}`;
      if (!inBounds(cx + dx, cz + dz)) continue;
      const g = gScore.get(k)! + cellCost(nk) + Math.abs(cellHeight(nk) - cellHeight(k)) / 60;
      if (g < (gScore.get(nk) ?? Infinity)) {
        gScore.set(nk, g);
        prev.set(nk, k);
        const [nx, nz] = [cx + dx, cz + dz];
        openH.push({ k: nk, f: g + Math.abs(tx - nx) + Math.abs(tz - nz) });
      }
    }
  }
  if (!found) return;
  const route: string[] = [toK];
  let c = toK;
  while (c !== fromK) { c = prev.get(c)!; route.push(c); }
  route.reverse();
  // smoothed corridor profile
  const prof = route.map((k) => cellHeight(k));
  for (let pass = 0; pass < 3; pass++)
    for (let i = 1; i < prof.length - 1; i++) prof[i] = (prof[i - 1] + prof[i] + prof[i + 1]) / 3;
  // flatten heights toward the profile + clear hazards within the corridor
  const HALF_W = 320;
  const n = field.n;
  for (let ri = 0; ri < route.length; ri++) {
    const [cx, cz] = parse(route[ri]);
    const wx = (cx + 0.5) * CELL, wz = (cz + 0.5) * CELL;
    const g0x = Math.max(0, Math.round((wx - HALF_W - field.minX) / GRID));
    const g1x = Math.min(n, Math.round((wx + HALF_W - field.minX) / GRID));
    const g0z = Math.max(0, Math.round((wz - HALF_W - field.minZ) / GRID));
    const g1z = Math.min(n, Math.round((wz + HALF_W - field.minZ) / GRID));
    for (let gz = g0z; gz <= g1z; gz++) {
      for (let gx = g0x; gx <= g1x; gx++) {
        const px = gx * GRID + field.minX, pz = gz * GRID + field.minZ;
        const d = Math.hypot(px - wx, pz - wz);
        const w = 1 - smoothstep(HALF_W * 0.45, HALF_W, d);
        if (w <= 0) continue;
        const i = gz * (n + 1) + gx;
        field.heights[i] = lerp(field.heights[i], prof[ri], w * 0.9);
        if (w > 0.4) { field.hazards[i] = HAZ_NONE; field.glow[i] *= 1 - w; }
      }
    }
  }
  // re-derive walkability for touched cells with EXACTLY the main-loop rules (evalCell): carving
  // changed heights AND hazard bytes, so stale entries must be overwritten and newly-failing cells
  // deleted — a one-sided update left wrong costs / phantom walkability (review finding)
  for (const k of route) {
    const [cx, cz] = parse(k);
    for (let dz = -2; dz <= 2; dz++) {
      for (let dx = -2; dx <= 2; dx++) {
        const nk = `${cx + dx},${cz + dz}`;
        const r = evalCell(cx + dx, cz + dz);
        if (r) {
          walk.set(nk, r.cost);
          cellH.set(nk, r.h);
        } else {
          walk.delete(nk);
          cellH.delete(nk);
        }
      }
    }
  }
}
